import {parseNumericInput} from './numericInput';

// Planning inputs are whole toman. Quote prices and all purchase accounting are
// native integer rial; the conversion happens only at this explicit boundary.
export const TOMAN_TO_RIAL = 10;
export const MIN_PURCHASE_TOMAN = 100000;
export const MAX_PLAN_TOMAN = Math.floor(Number.MAX_SAFE_INTEGER / TOMAN_TO_RIAL);
export const DEFAULT_TARGET_TOMAN = 2000000000;
export const MIN_PLAN_DAYS = 1;
export const DAYS_PER_PLAN_MONTH = 30;
export const MIN_PLAN_MONTHS = 1;
export const MAX_PLAN_MONTHS = 24;
export const DEFAULT_PLAN_MONTHS = 6;
export const MAX_PLAN_DAYS = MAX_PLAN_MONTHS * DAYS_PER_PLAN_MONTH;
export const METALS = {
  silver: {label: 'نقره', scene: 'silver'},
  gold_18k: {label: 'طلای ۱۸ عیار', scene: 'gold'},
  copper: {label: 'مس', scene: 'copper'},
};

export function planInteger(value, min = 0, max = MAX_PLAN_TOMAN) {
  const parsed = parseNumericInput(value);
  if (!parsed.valid || !/^\d+$/.test(parsed.value)) return null;
  const number = Number(parsed.value);
  return Number.isSafeInteger(number) && number >= min && number <= max ? number : null;
}

// Months are planning periods of exactly 30 days, not calendar dates.
export function planMonthsToDays(value) {
  const months = planInteger(value, MIN_PLAN_MONTHS, MAX_PLAN_MONTHS);
  return months === null ? null : months * DAYS_PER_PLAN_MONTH;
}

const divideUp = (numerator, denominator) => (numerator + denominator - BigInt(1)) / denominator;
const PRICE_SCALE_PER_MILLIGRAM = BigInt(100000);

export function quotePriceHundredths(value) {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  const matched = /^(\d+)(?:\.(\d{1,2}))?$/.exec(String(value));
  if (!matched) return null;
  const price = BigInt(matched[1]) * BigInt(100) + BigInt((matched[2] || '').padEnd(2, '0'));
  return price > BigInt(0) && price <= BigInt(Number.MAX_SAFE_INTEGER) ? price : null;
}

function metalForRial(amountRial, priceHundredths) {
  const amount = BigInt(amountRial);
  const milligrams = amount * PRICE_SCALE_PER_MILLIGRAM / priceHundredths;
  // Charge whole rial upward so a rounded purchase never exceeds its budget.
  const costRial = divideUp(milligrams * priceHundredths, PRICE_SCALE_PER_MILLIGRAM);
  if (milligrams > BigInt(Number.MAX_SAFE_INTEGER)) return null;
  return {
    milligrams: Number(milligrams),
    grams: Number(milligrams) / 1000,
    costRial: Number(costRial),
    cashRial: amountRial - Number(costRial),
  };
}

export function minimumPurchase(sellPriceRial) {
  const price = quotePriceHundredths(sellPriceRial);
  if (price === null) return null;
  const milligrams = (BigInt(MIN_PURCHASE_TOMAN * TOMAN_TO_RIAL) - BigInt(1)) * PRICE_SCALE_PER_MILLIGRAM / price + BigInt(1);
  const costRial = divideUp(milligrams * price, PRICE_SCALE_PER_MILLIGRAM);
  if (costRial > BigInt(Number.MAX_SAFE_INTEGER)) return null;
  return {milligrams: Number(milligrams), grams: Number(milligrams) / 1000, costRial: Number(costRial)};
}

export function createGoalPlan({targetToman, currentToman = 0, days, quote = null}) {
  const target = planInteger(targetToman, 1);
  const current = planInteger(currentToman);
  const duration = planInteger(days, MIN_PLAN_DAYS, MAX_PLAN_DAYS);
  const errors = {target: target === null, current: current === null, days: duration === null};
  if (Object.values(errors).some(Boolean)) return {valid: false, errors};

  const remainingToman = Math.max(0, target - current);
  const dailyToman = Number(divideUp(BigInt(remainingToman), BigInt(duration)));
  const remainingRial = remainingToman * TOMAN_TO_RIAL;
  const price = quotePriceHundredths(quote?.sellPriceRial);
  const minimum = minimumPurchase(quote?.sellPriceRial);
  const aggregate = price === null ? null : metalForRial(remainingRial, price);
  const quoteAvailable = Boolean(minimum && aggregate);
  let contributedToman = 0;
  let cashRial = 0;
  let purchasedMilligrams = 0;
  let spentRial = 0;
  let purchaseCount = 0;
  let firstPurchaseDay = null;
  const schedule = Array.from({length: duration}, (_, index) => {
    const contributionToman = Math.min(dailyToman, remainingToman - contributedToman);
    contributedToman += contributionToman;
    cashRial += contributionToman * TOMAN_TO_RIAL;
    let purchase = null;
    if (quoteAvailable && cashRial >= minimum.costRial) {
      const candidate = metalForRial(cashRial, price);
      if (candidate && candidate.costRial >= MIN_PURCHASE_TOMAN * TOMAN_TO_RIAL) {
        purchase = candidate;
        purchasedMilligrams += purchase.milligrams;
        spentRial += purchase.costRial;
        cashRial = purchase.cashRial;
        purchaseCount += 1;
        if (firstPurchaseDay === null) firstPurchaseDay = index + 1;
      }
    }
    return {
      day: index + 1, contributionToman, contributedToman,
      progress: Math.min(1, (Math.min(current, target) + contributedToman) / target),
      cashRial, purchasedMilligrams, spentRial, purchase,
    };
  });
  return {
    valid: true, errors, targetToman: target, currentToman: current, days: duration,
    remainingToman, dailyToman, lastDayToman: schedule[duration - 1].contributionToman,
    schedule, quoteAvailable, sellPriceRial: quoteAvailable ? quote.sellPriceRial : null,
    minimum: quoteAvailable ? minimum : null,
    // Aggregate is one conversion of all NEW contributions, not the sum of
    // rounded purchases and not a liquidation value of existing savings.
    aggregate: quoteAvailable ? aggregate : null,
    purchases: quoteAvailable ? {
      count: purchaseCount, firstDay: firstPurchaseDay,
      milligrams: purchasedMilligrams, grams: purchasedMilligrams / 1000,
      spentRial, cashRial,
    } : null,
  };
}

// Split only NEW contributions; existing savings have already been subtracted
// by createGoalPlan. Whole-toman remainders follow the canonical metal order.
export function createGoalAllocation({targetToman, currentToman = 0, days, metals, quotes = {}}) {
  const plan = createGoalPlan({targetToman, currentToman, days});
  const metalOrder = Object.keys(METALS);
  const validMetals = Array.isArray(metals) && metals.length >= 1 && metals.length <= metalOrder.length
    && metals.every(metal => metalOrder.includes(metal)) && new Set(metals).size === metals.length;
  const errors = {...plan.errors, metals: !validMetals};
  if (!plan.valid || !validMetals) return {valid: false, plan, allocations: [], errors};

  const selected = metalOrder.filter(metal => metals.includes(metal));
  const share = Math.floor(plan.remainingToman / selected.length);
  const remainder = plan.remainingToman % selected.length;
  const allocations = selected.map((metal, index) => {
    const amountToman = share + (index < remainder ? 1 : 0);
    // A completed or sub-toman share is already funded. Keep the same plan
    // shape, but do not invent a positive target or a quoted metal weight.
    const allocationPlan = amountToman === 0 ? {
      valid: true, errors: {target: false, current: false, days: false},
      targetToman: 0, currentToman: 0, days: plan.days,
      remainingToman: 0, dailyToman: 0, lastDayToman: 0,
      schedule: Array.from({length: plan.days}, (_, dayIndex) => ({
        day: dayIndex + 1, contributionToman: 0, contributedToman: 0, progress: 1,
        cashRial: 0, purchasedMilligrams: 0, spentRial: 0, purchase: null,
      })),
      quoteAvailable: false, sellPriceRial: null, minimum: null, aggregate: null, purchases: null,
    } : createGoalPlan({targetToman: amountToman, currentToman: 0, days: plan.days, quote: quotes?.[metal]});
    return {metal, amountToman, plan: allocationPlan};
  });
  return {valid: true, plan, allocations, errors};
}
